import { useCallback, useMemo, useState } from 'react';
import { Card } from 'components/Card/Card';
import { get } from 'lodash';
import {
  examV3ExamReservationSettingOnChange,
  examV3ExamReservationSettingOnClear,
  examV3ExamReservationSettingsErrorsSelector,
  examV3ExamReservationSettingsSelector,
  examV3ExamReservationSettingsValidate,
} from 'slices/examV3ExamReservationSettings.slice';
import { fieldsSelector } from 'slices/field.slice';
import { selectObjectTypesList } from 'slices/objectTypes.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import type { TReservationTemplate, TType } from '@timeedit/types';
import { Icon, TEInput, Tooltip } from '@timeedit/ui-components';
import {
  Collapse,
  type CollapseProps,
  Space,
} from '@timeedit/ui-components/antd';
import { getText } from '../../../../i18n/intl';
import {
  CommonValueInput,
  FieldsList,
  PublishingConfigShortMessage,
} from '../../components';
import { settingTypes } from '../../constants';
import { usePublishingConfigDialog } from '../../hooks';
import '../../components/ReservationDisplaySettings.scss';

type Props = {
  capacityReservationTemplate: TReservationTemplate;
  sizeReservationTemplate: TReservationTemplate;
};

const EditExamReservationSettings = (props: Props) => {
  const [selectedRows, setSelectedRows] = useState({
    fields: [],
    information: [],
  });
  const dispatch = useAppDispatch();
  const allFields = useAppSelector(fieldsSelector);
  const allObjectTypes: TType[] = useAppSelector(selectObjectTypesList);
  const config = useAppSelector(examV3ExamReservationSettingsSelector);
  const validationErrors = useAppSelector(
    examV3ExamReservationSettingsErrorsSelector
  );

  const onArrayFieldChange = useCallback(
    (dataPath: string) =>
      (index: number | undefined, key: string, value: any) => {
        let path: string;
        if (index === undefined) {
          // Single object path (seat/location)
          path = `${dataPath}.${key}`;
        } else {
          path = `${dataPath}[${index}].${key}`;
        }
        dispatch(
          examV3ExamReservationSettingOnChange({
            path,
            value,
          })
        );
        if (validationErrors != null) {
          dispatch(examV3ExamReservationSettingsValidate(false));
        }
      },
    [dispatch, validationErrors]
  );

  const {
    openSingle: openPublishingSingle,
    openIndexed: openPublishingIndexed,
    modalEl: publishingModal,
  } = usePublishingConfigDialog({ onArrayFieldChange });

  const onChange = useCallback(
    (path: string) => (value: any) => {
      if (value == null || value === undefined) {
        dispatch(examV3ExamReservationSettingOnClear({ path }));
      } else {
        dispatch(examV3ExamReservationSettingOnChange({ path, value }));
      }
      if (validationErrors != null) {
        dispatch(examV3ExamReservationSettingsValidate(false));
      }
    },
    [dispatch, validationErrors]
  );

  const onRowSelect = (section: keyof typeof selectedRows) => (rows: any) => {
    if (!rows.length && !selectedRows[section].length) return;
    setSelectedRows((prev) => ({ ...prev, [section]: rows }));
  };

  const onRowDelete =
    (section: keyof typeof selectedRows, dataPath: string) => () => {
      if (!selectedRows[section]?.length) {
        return;
      }

      const newSelectedRow = get(config, dataPath, []).filter(
        (row) => !selectedRows[section].includes(row)
      );

      onChange(dataPath)(newSelectedRow.length === 0 ? [] : newSelectedRow);
    };

  const getExtraColumns = useCallback(
    (path: 'information' | 'fields') => {
      return [
        {
          key: 'published',
          dataIndex: 'enabled',
          render: (_, record, index: number) => {
            const title =
              path === 'fields'
                ? `Extra information ${record.label ? `- ${record.label}` : ''}`
                : `Information block ${
                    record.title ? `- ${record.title}` : ''
                  }`;
            return (
              <PublishingConfigShortMessage
                enabled={record.enabled}
                timeDisplay={record.timeDisplay}
                onOpenModal={() =>
                  openPublishingIndexed(path, index, title, {
                    enabled: record.enabled,
                    timeDisplay: record.timeDisplay,
                  })
                }
              />
            );
          },
          title: (
            <div>
              Publish{' '}
              <Tooltip overlay={<PublishingConfigKey />}>
                <Icon id="QuestionCircleOutlined" alt="Info" />
              </Tooltip>
            </div>
          ),
          width: '150px',
        },
      ];
    },
    [openPublishingIndexed]
  );

  const extraColumnsFields = useMemo(
    () => getExtraColumns('fields'),
    [getExtraColumns]
  );
  const extraColumnsInformationBlocks = useMemo(
    () => getExtraColumns('information'),
    [getExtraColumns]
  );

  const fieldHasError =
    (path: string) =>
    (key: string, index: number, type: string): boolean => {
      if (
        type === 'object-field' &&
        !config?.[path]?.[index]?.[key]?.includes('-')
      ) {
        return true;
      }
      return validationErrors?.[`${path},${index},${key}`] != null;
    };

  const collapseItems: CollapseProps['items'] = [
    {
      key: 'main',
      label: 'Main',
      children: (
        <div className="reservation-display-settings-main-container">
          <SettingCard
            required
            title="Exam name"
            type={config?.examLabel}
            reservationTemplate={props.sizeReservationTemplate}
            path="examLabel"
            validationErrors={validationErrors}
            onChangeCreator={onChange}
          />

          <SettingCard
            required
            title="Exam type"
            type={config?.examType}
            reservationTemplate={props.sizeReservationTemplate}
            path="examType"
            validationErrors={validationErrors}
            onChangeCreator={onChange}
          />

          <Card className="reservation-display-settings-flex-grow" title="Seat">
            <TEInput.LabelWrapper
              label="Publish"
              // @ts-expect-error Expects a string
              tooltip={<PublishingConfigKey />}
            >
              <PublishingConfigShortMessage
                enabled={config.seat?.enabled}
                timeDisplay={config.seat?.timeDisplay}
                onOpenModal={() =>
                  openPublishingSingle('seat', 'Seat', {
                    enabled: config.seat?.enabled,
                    timeDisplay: config.seat?.timeDisplay,
                  })
                }
              />
            </TEInput.LabelWrapper>
          </Card>

          <Card
            title="Location"
            className="reservation-display-settings-flex-grow reservation-display-settings-location-card"
          >
            <div style={{ paddingBottom: '10px' }}>
              <TEInput.LabelWrapper
                label="Publish"
                // @ts-expect-error Expects a string
                tooltip={<PublishingConfigKey />}
              >
                <PublishingConfigShortMessage
                  enabled={config.location?.enabled}
                  timeDisplay={config.location?.timeDisplay}
                  onOpenModal={() =>
                    openPublishingSingle('location', 'Location', {
                      enabled: config.location?.enabled,
                      timeDisplay: config.location?.timeDisplay,
                    })
                  }
                />
              </TEInput.LabelWrapper>
            </div>
            <div className="container">
              <SettingCard
                title="Room"
                type={config?.location?.room}
                reservationTemplate={props.sizeReservationTemplate}
                path="location.room"
                validationErrors={validationErrors}
                onChangeCreator={onChange}
              />
              <SettingCard
                title="Building"
                type={config?.location?.building}
                reservationTemplate={props.sizeReservationTemplate}
                path="location.building"
                validationErrors={validationErrors}
                onChangeCreator={onChange}
              />
              <SettingCard
                title="Campus"
                type={config?.location?.campus}
                reservationTemplate={props.sizeReservationTemplate}
                path="location.campus"
                validationErrors={validationErrors}
                onChangeCreator={onChange}
              />
            </div>
          </Card>
        </div>
      ),
    },
    {
      key: 'fields',
      label: 'Extra information',
      children: (
        <FieldsList
          extraColumns={extraColumnsFields}
          disableWidth
          columnSet="labelAndWidth"
          types={settingTypes}
          onChange={onChange('fields')}
          onFieldChange={onArrayFieldChange('fields')}
          onAddRow={() => {
            onChange('fields')([
              ...(config?.fields ?? []),
              { id: uuidv4(), enabled: false },
            ]);
          }}
          values={config?.fields}
          reservationTemplate={props.sizeReservationTemplate}
          allTypes={allObjectTypes}
          allFields={allFields}
          objectTypeIds={
            props.sizeReservationTemplate?.types?.map((type) => type.type) ?? []
          }
          extraOptions={[]}
          onRowSelect={onRowSelect('fields')}
          onRowDelete={onRowDelete('fields', 'fields')}
          hasError={fieldHasError('fields')}
        />
      ),
    },
    {
      key: 'information',
      label: 'Information blocks',
      children: (
        <FieldsList
          extraColumns={extraColumnsInformationBlocks}
          disableWidth
          types={settingTypes}
          onChange={onChange('information')}
          onFieldChange={onArrayFieldChange('information')}
          onAddRow={() => {
            onChange('information')([
              ...(config?.information ?? []),
              { id: uuidv4(), enabled: false },
            ]);
          }}
          values={config?.information as any}
          reservationTemplate={props.sizeReservationTemplate}
          allTypes={allObjectTypes}
          allFields={allFields}
          objectTypeIds={
            props.sizeReservationTemplate?.types?.map((type) => type.type) ?? []
          }
          extraOptions={[]}
          onRowSelect={onRowSelect('information')}
          onRowDelete={onRowDelete('information', 'information')}
          hasError={fieldHasError('information')}
        />
      ),
    },
  ];

  return (
    <>
      <Collapse
        style={{ margin: '10px' }}
        items={collapseItems}
        defaultActiveKey={['main', 'fields', 'information']}
      />
      {publishingModal}
    </>
  );
};

const PublishingConfigKey = () => {
  return (
    <div>
      <div>
        <strong>Not Published:</strong> This information will never be shown
      </div>
      <div>
        <strong>Days/hours/minutes before:</strong> This information will be
        shown some time before the start time of the exam reservation.
      </div>
      <div>
        <strong>Immediately:</strong> This information will be shown immediately
        when the exam reservation is available
      </div>
    </div>
  );
};

const SettingCard = (props: {
  title: string;
  reservationTemplate: TReservationTemplate;
  fieldLabel?: string;
  objectLabel?: string;
  type: any;
  path: string;
  validationErrors: Record<string, z.ZodIssue>;
  onChangeCreator: (path: string) => (newValue: any) => void;
  required?: boolean;
}) => {
  const allFields = useAppSelector(fieldsSelector);
  const allObjectTypes: TType[] = useAppSelector(selectObjectTypesList);

  const errorPath = props.path.replace('.', ',');

  const hasError = useCallback(
    (path: string) => {
      return props.validationErrors?.[path] != null;
    },
    [props.validationErrors]
  );

  const status = useCallback(
    (path: string): 'error' | undefined => {
      return hasError(path) ? 'error' : undefined;
    },
    [hasError, props.validationErrors]
  );

  return (
    <Card
      title={
        <>
          {props.title}{' '}
          {props.required && (
            <span className="reservation-display-settings-required">*</span>
          )}
        </>
      }
      size="small"
      className="reservation-display-settings-flex-grow"
    >
      <Space
        direction="vertical"
        className="reservation-display-settings-full-width"
      >
        <TEInput.Select
          label="Type"
          value={props.type?.type}
          options={settingTypes}
          onChange={(val) => {
            props.onChangeCreator(`${props.path}.type`)(val);
            props.onChangeCreator(`${props.path}.value`)(null);
          }}
          status={status(`${errorPath}`)}
        />
        <CommonValueInput
          allFields={allFields}
          allTypes={allObjectTypes}
          reservationTemplate={props.reservationTemplate}
          type={{ ...props.type, label: '' }}
          onChange={props.onChangeCreator(`${props.path}.value`)}
          fieldLabel={props.fieldLabel ?? getText('field')}
          objectLabel={props.objectLabel ?? getText('objects_type')}
          valueLabel="Text"
          objectTypeIds={props.reservationTemplate?.types.map(
            (type) => type.type
          )}
          hasError={hasError(`${errorPath},value`)}
        />
      </Space>
    </Card>
  );
};

export default EditExamReservationSettings;
